// src/Views/Admin/AdminAnnouncements.js
// UPDATED: Added Push Notification toggle alongside Email toggle
// - sendViaEmail: triggers email Cloud Function
// - sendPush: triggers push notification Cloud Function

import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { 
    collection, 
    addDoc, 
    serverTimestamp, 
    query, 
    orderBy, 
    onSnapshot, 
    deleteDoc, 
    doc 
} from 'firebase/firestore';
import { db } from '../../firebase';
import './AdminAnnouncements.css';

const AdminAnnouncements = () => {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [category, setCategory] = useState('general');
    const [type, setType] = useState('info');
    const [audience, setAudience] = useState('all');
    const [sendViaEmail, setSendViaEmail] = useState(false);
    const [sendPush, setSendPush] = useState(false); // 🔔 NEW: Push toggle
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);

    // 1. Fetch History
    useEffect(() => {
        const q = query(collection(db, 'announcements'), orderBy('createdAt', 'desc'));
        const unsub = onSnapshot(q, (snap) => {
            setHistory(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsub();
    }, []);

    // 2. Get Subject based on category
    const getSubject = (cat) => {
        switch (cat) {
            case 'football':
                return 'Football Update';
            case 'crypto':
                return 'Crypto Update';
            default:
                return 'Announcement';
        }
    };

    // 3. Handle Broadcast
    const handleBroadcast = async (e) => {
        e.preventDefault();
        if (!title || !message) return;

        // 🔥 Capture checkboxes before async
        const shouldEmail = sendViaEmail === true;
        const shouldPush = sendPush === true;

        setLoading(true);
        try {
            await addDoc(collection(db, 'announcements'), {
                title,
                message, 
                body: message,
                preview: message.substring(0, 100), // For push notification body
                category,
                subject: getSubject(category),
                type,
                targetAudience: audience,
                source: 'broadcast',
                sendViaEmail: shouldEmail, // 🔥 Triggers email Cloud Function
                sendPush: shouldPush,       // 🔔 Triggers push Cloud Function
                createdAt: serverTimestamp(),
            });

            // Reset Form
            setTitle('');
            setMessage('');
            setCategory('general');
            setType('info');
            setAudience('all');
            setSendViaEmail(false);
            setSendPush(false);
            
            // Dynamic success message
            let successMsg = '📡 Broadcast saved';
            if (shouldEmail && shouldPush) {
                successMsg = '📧🔔 Broadcast sent + Emails & Push queued!';
            } else if (shouldEmail) {
                successMsg = '📧 Broadcast sent + Emails queued!';
            } else if (shouldPush) {
                successMsg = '🔔 Broadcast sent + Push notifications queued!';
            }
            
            alert(successMsg);
        } catch (err) {
            console.error('Broadcast failed:', err);
            alert('Error sending broadcast: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Delete this broadcast?')) {
            await deleteDoc(doc(db, 'announcements', id));
        }
    };

    // Get badge color based on category
    const getCategoryBadgeClass = (cat) => {
        switch (cat) {
            case 'football':
                return 'admin-announce__badge--success';
            case 'crypto':
                return 'admin-announce__badge--warning';
            default:
                return 'admin-announce__badge--info';
        }
    };

    return (
        <div className="admin-announce">
            <div className="admin-announce__header">
                <h1 className="admin-announce__title">Broadcast Center</h1>
                <p className="admin-announce__subtitle">Send global announcements to your users.</p>
            </div>

            <div className="admin-announce__card">
                <form onSubmit={handleBroadcast}>
                    {/* Row 1: Title */}
                    <div className="admin-announce__group">
                        <label className="admin-announce__label">Title</label>
                        <input 
                            type="text" 
                            className="admin-announce__input"
                            placeholder="e.g. New Feature Released"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                        />
                    </div>

                    {/* Row 2: Category & Style */}
                    <div className="admin-announce__row">
                        <div className="admin-announce__group" style={{flex: 1}}>
                            <label className="admin-announce__label">Category</label>
                            <select 
                                className="admin-announce__select"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                <option value="general">📢 General Announcement</option>
                                <option value="football">⚽ Football Update</option>
                                <option value="crypto">📈 Crypto Update</option>
                            </select>
                        </div>
                        <div className="admin-announce__group" style={{flex: 1}}>
                            <label className="admin-announce__label">Style</label>
                            <select 
                                className="admin-announce__select"
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                            >
                                <option value="info">🔵 Info (Blue)</option>
                                <option value="success">🟢 Success (Green)</option>
                                <option value="warning">🟠 Warning (Orange)</option>
                            </select>
                        </div>
                    </div>

                    {/* Row 3: Audience Selector */}
                    <div className="admin-announce__group">
                        <label className="admin-announce__label">Target Audience</label>
                        <select 
                            className="admin-announce__select"
                            value={audience}
                            onChange={(e) => setAudience(e.target.value)}
                            style={{ borderColor: audience === 'premium' ? '#f59e0b' : '#334155' }}
                        >
                            <option value="all">🌍 All Users (Free & Premium)</option>
                            <option value="premium">💎 Premium Users Only</option>
                        </select>
                    </div>

                    {/* Row 4: Message */}
                    <div className="admin-announce__group">
                        <label className="admin-announce__label">Message</label>
                        <textarea 
                            className="admin-announce__textarea"
                            placeholder="Type your announcement here..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            required
                        />
                    </div>

                    {/* Preview */}
                    <div style={{
                        background: 'rgba(30, 41, 59, 0.5)',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '1rem',
                        border: '1px solid rgba(71, 85, 105, 0.3)'
                    }}>
                        <p style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '4px' }}>Preview:</p>
                        <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
                            <strong style={{ color: '#e2e8f0' }}>{getSubject(category)}</strong>
                            {title && <span> — {title}</span>}
                        </p>
                    </div>

                    {/* 🔔 NOTIFICATION OPTIONS */}
                    <div style={{
                        background: 'rgba(30, 41, 59, 0.5)',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '1rem',
                        border: '1px solid rgba(71, 85, 105, 0.3)'
                    }}>
                        <p style={{ color: '#e2e8f0', fontSize: '0.85rem', marginBottom: '12px', fontWeight: '600' }}>
                            📣 Delivery Options
                        </p>

                        {/* Push Notification Checkbox */}
                        <div style={{
                            background: sendPush ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                            borderRadius: '6px',
                            padding: '10px 12px',
                            marginBottom: '8px',
                            border: sendPush ? '1px solid #3b82f6' : '1px solid rgba(71, 85, 105, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <input 
                                type="checkbox"
                                id="sendPush"
                                checked={sendPush}
                                onChange={(e) => setSendPush(e.target.checked)}
                                style={{ 
                                    width: '18px', 
                                    height: '18px',
                                    accentColor: '#3b82f6'
                                }}
                            />
                            <label 
                                htmlFor="sendPush"
                                style={{ 
                                    color: sendPush ? '#60a5fa' : '#94a3b8',
                                    fontWeight: sendPush ? '600' : 'normal',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    flex: 1
                                }}
                            >
                                🔔 Send Push Notification
                                {sendPush && <span style={{ color: '#60a5fa', marginLeft: '8px' }}>(iOS users will receive alert)</span>}
                            </label>
                        </div>

                        {/* Email Checkbox */}
                        <div style={{
                            background: sendViaEmail ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                            borderRadius: '6px',
                            padding: '10px 12px',
                            border: sendViaEmail ? '1px solid #ef4444' : '1px solid rgba(71, 85, 105, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <input 
                                type="checkbox"
                                id="sendViaEmail"
                                checked={sendViaEmail}
                                onChange={(e) => setSendViaEmail(e.target.checked)}
                                style={{ 
                                    width: '18px', 
                                    height: '18px',
                                    accentColor: '#ef4444'
                                }}
                            />
                            <label 
                                htmlFor="sendViaEmail"
                                style={{ 
                                    color: sendViaEmail ? '#ef4444' : '#94a3b8',
                                    fontWeight: sendViaEmail ? '600' : 'normal',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    flex: 1
                                }}
                            >
                                {sendViaEmail 
                                    ? '⚠️ WILL EMAIL ALL USERS!' 
                                    : '📧 Send via Email'}
                            </label>
                        </div>
                    </div>

                    {/* Summary of what will happen */}
                    {(sendPush || sendViaEmail) && (
                        <div style={{
                            background: 'rgba(251, 191, 36, 0.1)',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '1rem',
                            border: '1px solid rgba(251, 191, 36, 0.3)'
                        }}>
                            <p style={{ color: '#fbbf24', fontSize: '0.85rem', margin: 0 }}>
                                ⚡ This broadcast will:
                                {sendPush && <span style={{ display: 'block', marginLeft: '16px' }}>• Send push notification to iOS users</span>}
                                {sendViaEmail && <span style={{ display: 'block', marginLeft: '16px' }}>• Send email to all users</span>}
                                <span style={{ display: 'block', marginLeft: '16px' }}>• Save to announcements feed</span>
                            </p>
                        </div>
                    )}

                    <button type="submit" className="admin-announce__btn" disabled={loading}>
                        {loading ? 'Sending...' : (
                            <>
                                <Icon icon="solar:satellite-bold" />
                                <span>Broadcast {getSubject(category)}</span>
                            </>
                        )}
                    </button>
                </form>
            </div>

            <div className="admin-announce__history">
                <h3 className="admin-announce__h-title">Recent Broadcasts</h3>
                <div className="admin-announce__list">
                    {history.length === 0 ? (
                        <p style={{ color: '#64748b', textAlign: 'center', padding: '1rem' }}>
                            No broadcasts yet.
                        </p>
                    ) : (
                        history.map(item => (
                            <div key={item.id} className="admin-announce__item">
                                <div className="admin-announce__info">
                                    <h4>
                                        <span className={`admin-announce__badge ${getCategoryBadgeClass(item.category || item.type)}`}>
                                            {item.subject || item.type || 'Announcement'}
                                        </span>
                                        {/* 🔔 Push badge */}
                                        {item.sendPush && (
                                            <span 
                                                className="admin-announce__badge" 
                                                style={{
                                                    marginLeft: '6px',
                                                    background: 'rgba(59, 130, 246, 0.2)',
                                                    color: '#60a5fa',
                                                    border: '1px solid rgba(59, 130, 246, 0.3)'
                                                }}
                                            >
                                                🔔 PUSH {item.pushSuccessCount ? `(${item.pushSuccessCount})` : ''}
                                            </span>
                                        )}
                                        {/* 📧 Email badge */}
                                        {item.sendViaEmail && (
                                            <span className="admin-announce__badge admin-announce__badge--info" style={{marginLeft: '6px'}}>
                                                📧 EMAILED {item.sentCount ? `(${item.sentCount})` : ''}
                                            </span>
                                        )}
                                        {/* 💎 Premium badge */}
                                        {item.targetAudience === 'premium' && (
                                            <span className="admin-announce__badge admin-announce__badge--warning" style={{marginLeft: '6px'}}>
                                                PREMIUM ONLY
                                            </span>
                                        )}
                                        <span style={{marginLeft: '8px'}}>{item.title}</span>
                                    </h4>
                                    <p>{item.message}</p>
                                    <small style={{ color: '#64748b', fontSize: '0.7rem' }}>
                                        {item.createdAt?.toDate?.()?.toLocaleString() || 'Just now'}
                                        {item.pushSentAt && ` • Push sent ${item.pushSentAt?.toDate?.()?.toLocaleTimeString() || ''}`}
                                    </small>
                                </div>
                                <button className="admin-announce__delete" onClick={() => handleDelete(item.id)}>
                                    <Icon icon="solar:trash-bin-trash-linear" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminAnnouncements;